import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {AddOutlined, DeleteOutlined, EditOutlined} from "@mui/icons-material";
import {Box} from "@mui/material";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {useAppDispatch, useAppSelector} from "../../../../redux";
import Api from "../../../../../core/services/api/api";
import {VendorProductsDataGridColumnNames} from "../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState,} from "../../../../../packages/data-grid";
import {DefaultPaginationInfo} from "../../../../../core/constants/enums";
import useSearch from "../../../../hooks/use-search";
import {formatMoney} from "../../../../../core/services/utils/utils";
import DateUtils from "../../../../../core/services/date-utils";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import IconButton from "../../../../components/base/button/icon_button";
import PartnerDataGrid from "../../../../containers/data-grid";
import UpsertVendorPartDialog from "../../../../components/dialogs/upsert-vendor-part";
import MultiSelectPartSearchSection from "../../../../components/text-fields/autocomplete/part-autocomplete/multi-select-part-search-section";
import {ListPageShell, PageActions, SearchContainer} from "../../../../components/page-shell";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../hooks/use-access-levels";
import ProductInformationPopover from "../../../../components/app-specific/draggable-popover/product-information-popover";

const exportingChunkSize = 100;

const dataGridSaveConfig = {
    name: 'VendorProductsViewDataGrid',
    version: 1,
};

const initUpsertVendorPartDialog = {open: false, record: null};

/**
 * Vendor-scoped products list — all parts offered by the current vendor.
 */
const VendorProductsView = () => {
    const {vendorId} = useParams();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-products'});
    const defaultVendor = useAppSelector((state) => state.vendorInformation);
    const [data, setData] = useState([]);
    const [upsertVendorPartDialogState, setUpsertVendorPartDialogState] = useState(initUpsertVendorPartDialog);
    const [removingIds, setRemovingIds] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();
    const {configureNotebookTags} = useConfigureNotebook('vendor-products');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canManageProducts = operatorAccessLevels.packages.base.manageVendorProducts;
    const canExportProducts = operatorAccessLevels.packages.base.exportVendorProducts;

    const numericVendorId = useMemo(() => Number(vendorId), [vendorId]);

    useEffect(() => {
        if (!defaultVendor?.code) {
            return;
        }
        configureNotebookTags((tags) => tags.vendorProducts(defaultVendor.code));
        configureBehavioralTool((tags) => tags.vendorProducts(defaultVendor.code));
    }, [configureBehavioralTool, configureNotebookTags, defaultVendor?.code]);

    const columns = useMemo(() => /** @type {DataGridColumn[]} */ ([
        {
            name: VendorProductsDataGridColumnNames.remove,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.element,
            sortable: false,
            pinnedToggleable: false,
            resizable: false,
            visibilityToggleable: false,
            reorderable: false,
            pinned: true,
            width: 0,
        },
        {
            name: VendorProductsDataGridColumnNames.edit,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.element,
            sortable: false,
            pinnedToggleable: false,
            resizable: false,
            visibilityToggleable: false,
            reorderable: false,
            pinned: true,
            width: 0,
        },
        {
            title: t('columns.partNo'),
            name: VendorProductsDataGridColumnNames.partNo,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: t('columns.vendorPartNumber'),
            name: VendorProductsDataGridColumnNames.vendorPartNumber,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: t('columns.vendorPrice'),
            name: VendorProductsDataGridColumnNames.vendorPrice,
            alignment: DataGridColumnAlignments.right,
            type: DataGridColumnTypes.money,
            sortable: false,
        },
        {
            title: t('columns.startDateTime'),
            name: VendorProductsDataGridColumnNames.startDateTime,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.dateTime,
            sortable: false,
        },
        {
            title: t('columns.endDateTime'),
            name: VendorProductsDataGridColumnNames.endDateTime,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.dateTime,
            sortable: false,
        },
        {
            title: t('columns.isCurrentlyActive'),
            name: VendorProductsDataGridColumnNames.isCurrentlyActive,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.boolean,
            sortable: false,
            width: 140,
        },
        {
            title: t('columns.submitter'),
            name: VendorProductsDataGridColumnNames.submitter,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: t('columns.submitDateTime'),
            name: VendorProductsDataGridColumnNames.submitDateTime,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.dateTime,
            sortable: false,
        },
        {
            title: t('columns.updater'),
            name: VendorProductsDataGridColumnNames.updater,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: t('columns.updateDateTime'),
            name: VendorProductsDataGridColumnNames.updateDateTime,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.dateTime,
            sortable: false,
        },
    ]).filter((column) => canManageProducts || (
        column.name !== VendorProductsDataGridColumnNames.remove &&
        column.name !== VendorProductsDataGridColumnNames.edit
    )), [canManageProducts, t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize
        ? {...DefaultPaginationInfo, pageSize: dataGridPageSize}
        : DefaultPaginationInfo, [dataGridPageSize]);

    /**
     * Loads vendor parts for the scoped vendor (optional part filter).
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        if (!Number.isFinite(numericVendorId)) {
            return;
        }
        const partIds = (args.filters?.parts ?? [])
            .map((part) => Number(part?.id))
            .filter((id) => Number.isFinite(id));
        const response = await Api.listVendorParts({
            pagination: args.paginationInfo,
            vendorIds: [numericVendorId],
            partIds,
        }, args.cancelTokenSource?.token);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport) {
            return response;
        }
    }, [isMounted, numericVendorId]);

    /**
     * useSearch entry point.
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings);

    /**
     * CSV export of the full result set.
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            const items = res?.data?.items ?? res?.data ?? [];
            if (!items.length) {
                break;
            }
            entries.push(...items);
            if (!isMounted()) {
                return;
            }
            _paginationInfo.currentPage++;
        }
        if (!entries.length) {
            return;
        }
        return {
            title: t('exportTitle', {name: defaultVendor?.name ?? defaultVendor?.code ?? vendorId}),
            body: entries.map((e, i) => ({
                '': i + 1,
                "Connection/Conflict ID": e.id,
                [t('columns.partNo')]: e.partNo ?? e.partNumber ?? "",
                [t('columns.vendorPartNumber')]: e.vendorPartNumber ?? "",
                [t('columns.vendorPrice')]: e.vendorPrice ? formatMoney(e.vendorPrice) : "",
                [t('columns.startDateTime')]: e.startDateTime ? DateUtils.serverToLocal(e.startDateTime) : "",
                [t('columns.endDateTime')]: e.endDateTime ? DateUtils.serverToLocal(e.endDateTime) : "",
                [t('columns.isCurrentlyActive')]: e.isCurrentlyActive ? 'Yes' : 'No',
                [t('columns.submitter')]: e.submitter ?? "",
                [t('columns.submitDateTime')]: e.submitDateTime ? DateUtils.serverToLocal(e.submitDateTime) : "",
                [t('columns.updater')]: e.updater ?? "",
                [t('columns.updateDateTime')]: e.updateDateTime ? DateUtils.serverToLocal(e.updateDateTime) : "",
            })),
        };
    }, [defaultVendor?.code, defaultVendor?.name, fetchEntries, isMounted, searchData, t, vendorId]);

    /**
     * Confirms and deletes a vendor part row.
     */
    const getRemoveConfirmation = useCallback((record) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: t('removeTitle', {
                partNo: record.partNo ?? record.partNumber ?? '--',
                vendorPartNumber: record.vendorPartNumber ?? '--',
            }),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return;
                }
                setRemovingIds((prevState) => [...prevState, record.id]);
                const response = await Api.deleteVendorPart(record.id);
                if (!isMounted()) {
                    return;
                }
                setRemovingIds((prevState) => prevState.filter((e) => e !== record.id));
                if (response?.resultFlag) {
                    setData((prevState) => prevState.filter((e) => e.id !== record.id));
                }
                return response?.resultFlag ?? false;
            },
        }));
    }, [dispatch, isMounted, t]);

    /**
     * Opens the upsert dialog for create (record omit) or edit.
     */
    const openUpsertDialog = useCallback((record = null) => {
        setUpsertVendorPartDialogState({open: true, record});
    }, []);

    /**
     * Applies create/update/remove results to the local grid.
     */
    const onUpsertSuccess = useCallback((upsertedRecord, removedRecordIds) => {
        if (removedRecordIds?.length) {
            searchData.setPaginationInfo((prev) => ({
                ...prev,
                length: Math.max(prev.length - removedRecordIds.length, 0),
            }));
        }
        setData((prevState) => {
            const newState = [...prevState];
            if (removedRecordIds?.length) {
                for (const removedId of removedRecordIds) {
                    const index = newState.findIndex((e) => e.id === removedId);
                    if (index !== -1) {
                        newState.splice(index, 1);
                    }
                }
            }
            const recordIndex = prevState.findIndex((e) => e.id === upsertedRecord.id);
            if (recordIndex === -1) {
                return [upsertedRecord, ...newState];
            }
            newState[recordIndex] = upsertedRecord;
            return newState;
        });
    }, [searchData]);

    const dataGridRows = useMemo(() => (
        data?.map((item) => ({
            key: item.id,
            data: item,
            cells: {
                [VendorProductsDataGridColumnNames.remove]: (
                    <IconButton
                        color={'paper.error'}
                        disabled={removingIds.includes(item.id)}
                        onPressed={() => getRemoveConfirmation(item)}
                    >
                        <DeleteOutlined className={'icon small'}/>
                    </IconButton>
                ),
                [VendorProductsDataGridColumnNames.edit]: (
                    <IconButton
                        color={'paper.primary'}
                        disabled={removingIds.includes(item.id)}
                        onPressed={() => openUpsertDialog(item)}
                    >
                        <EditOutlined className={'icon small'}/>
                    </IconButton>
                ),
                [VendorProductsDataGridColumnNames.partNo]: (
                    <ProductInformationPopover partNo={item.partNo ?? item.partNumber}>
                        {item.partNo ?? item.partNumber ?? ""}
                    </ProductInformationPopover>
                ),
                [VendorProductsDataGridColumnNames.vendorPartNumber]: item.vendorPartNumber,
                [VendorProductsDataGridColumnNames.vendorPrice]: item.vendorPrice,
                [VendorProductsDataGridColumnNames.startDateTime]: item.startDateTime,
                [VendorProductsDataGridColumnNames.endDateTime]: item.endDateTime,
                [VendorProductsDataGridColumnNames.isCurrentlyActive]: item.isCurrentlyActive ?? false,
                [VendorProductsDataGridColumnNames.submitter]: item.submitter,
                [VendorProductsDataGridColumnNames.submitDateTime]: item.submitDateTime,
                [VendorProductsDataGridColumnNames.updater]: item.updater,
                [VendorProductsDataGridColumnNames.updateDateTime]: item.updateDateTime,
            },
        }))
    ), [data, getRemoveConfirmation, openUpsertDialog, removingIds]);

    const title = useMemo(() => (
        defaultVendor?.name
            ? t('titleVendor', {name: defaultVendor.name})
            : t('title')
    ), [defaultVendor?.name, t]);

    const actionItems = useMemo(() => canManageProducts ? ([
        {
            id: 'add-vendor-product',
            label: t('addRecord'),
            icon: <AddOutlined className={'icon small'}/>,
            onClick: () => openUpsertDialog(),
        },
    ]) : [], [canManageProducts, openUpsertDialog, t]);

    /**
     * Bridges MultiSelectPartSearchSection's setState-style updater into list filters.
     */
    const setFilterParts = useCallback((updater) => {
        searchData.setFilters((prevState) => {
            const current = prevState.parts ?? [];
            const next = typeof updater === 'function' ? updater(current) : (updater ?? []);
            return {
                ...prevState,
                parts: next,
            };
        });
    }, [searchData]);

    const filters = useMemo(() => (
        <SearchContainer>
            <Box sx={{width: '100%'}}>
                <MultiSelectPartSearchSection
                    includeDefaultPart={false}
                    parts={searchData.filters?.parts ?? []}
                    setParts={setFilterParts}
                />
            </Box>
        </SearchContainer>
    ), [searchData.filters?.parts, setFilterParts]);

    return (
        <>
            <ListPageShell
                title={title}
                actions={<PageActions items={actionItems}/>}
                filters={filters}
            >
                <Box sx={{width: '100%'}}>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns}}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        getExportInfo={canExportProducts ? exportToCsv : undefined}
                        state={{
                            pagination: searchData.paginationInfo,
                            loading: {state: searchData.loading},
                            density: dataGridDensity,
                        }}
                    />
                </Box>
            </ListPageShell>
            <UpsertVendorPartDialog
                vendorId={numericVendorId}
                vendorName={defaultVendor?.name}
                vendorCode={defaultVendor?.code}
                close={() => setUpsertVendorPartDialogState(initUpsertVendorPartDialog)}
                onSuccess={onUpsertSuccess}
                {...upsertVendorPartDialogState}
            />
        </>
    );
};

export default VendorProductsView;
